import { Check, Shuffle } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  AvatarCandidate,
  DEFAULT_RETRY_AFTER_SECONDS,
  RegistrationApiError
} from '../pages/register/api/registration.api';

const TILE_COUNT = 9;
/** Sets kept for this page visit, to show again while the server is limiting us. */
const MAX_KEPT_SETS = 10;

type Status = 'loading' | 'ready' | 'failed';

interface SecurityAvatarPickerProps {
  /**
   * Asks the server for a set of candidates. A 429 must reject with a
   * `RegistrationApiError` carrying `retryAfterSeconds`.
   */
  loadCandidates: () => Promise<{ candidates: AvatarCandidate[] }>;
  /** Marks the picker as a required field. */
  required: boolean;
  /** Token of the picked avatar, or null when nothing is picked. */
  value: string | null;
  onChange: (token: string | null) => void;
  /**
   * Changing this drops every kept set and loads a fresh one, for when the
   * server rejected the picked token.
   */
  refreshKey?: number;
}

/**
 * Nine avatars from the server. Shuffle asks for a new set; while the server
 * is rate limiting, Shuffle instead cycles through the sets already received
 * on this page visit, oldest first, until the wait is over. Tokens stay
 * valid across sets, so the pick survives any Shuffle.
 */
export function SecurityAvatarPicker({
  loadCandidates,
  required,
  value,
  onChange,
  refreshKey = 0
}: Readonly<SecurityAvatarPickerProps>) {
  const [sets, setSets] = useState<AvatarCandidate[][]>([]);
  const [shown, setShown] = useState<number | null>(null);
  const [status, setStatus] = useState<Status>('loading');
  const [secondsLeft, setSecondsLeft] = useState(0);

  // Mirrors of state for use inside async callbacks.
  const setsRef = useRef<AvatarCandidate[][]>([]);
  const shownRef = useRef<number | null>(null);
  const limitedUntilRef = useRef(0);
  const cycleCursorRef = useRef(0);
  const requestIdRef = useRef(0);
  const waitTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>();
  const loadRef = useRef(loadCandidates);
  loadRef.current = loadCandidates;

  const show = (nextSets: AvatarCandidate[][], index: number | null) => {
    setsRef.current = nextSets;
    shownRef.current = index;
    setSets(nextSets);
    setShown(index);
  };

  /** Shows the next kept set, oldest first, never the one already on screen. */
  const cycleKept = () => {
    const kept = setsRef.current;
    if (kept.length === 0) return false;
    let next = cycleCursorRef.current % kept.length;
    if (next === shownRef.current && kept.length > 1) {
      next = (next + 1) % kept.length;
    }
    cycleCursorRef.current = next + 1;
    show(kept, next);
    setStatus('ready');
    return true;
  };

  const requestNow = useCallback(async () => {
    const requestId = ++requestIdRef.current;
    setStatus('loading');
    try {
      const { candidates } = await loadRef.current();
      if (requestId !== requestIdRef.current) return;
      const nextSets = [...setsRef.current, candidates.slice(0, TILE_COUNT)];
      const trimmed = nextSets.slice(-MAX_KEPT_SETS);
      show(trimmed, trimmed.length - 1);
      setStatus('ready');
    } catch (error) {
      if (requestId !== requestIdRef.current) return;
      if (error instanceof RegistrationApiError && error.status === 429) {
        const seconds = error.retryAfterSeconds ?? DEFAULT_RETRY_AFTER_SECONDS;
        limitedUntilRef.current = Date.now() + seconds * 1000;
        cycleCursorRef.current = 0;
      }
      if (!cycleKept()) setStatus('failed');
    }
  }, []);

  /**
   * Fetches a set. While the server is still limiting us the skeleton shows
   * and the request goes out as soon as the wait is over.
   */
  const fetchSet = useCallback(() => {
    clearTimeout(waitTimerRef.current);
    const wait = limitedUntilRef.current - Date.now();
    if (wait <= 0) {
      void requestNow();
      return;
    }
    // Any response still in flight is now stale.
    requestIdRef.current += 1;
    setStatus('loading');
    waitTimerRef.current = setTimeout(() => void requestNow(), wait);
  }, [requestNow]);

  useEffect(() => {
    show([], null);
    cycleCursorRef.current = 0;
    fetchSet();
    return () => {
      // A response or a pending wait from before unmount or a refresh is dropped.
      requestIdRef.current += 1;
      clearTimeout(waitTimerRef.current);
    };
  }, [refreshKey, fetchSet]);

  // In the retry state, counts down the server's wait so Try again opens
  // only when a request can succeed.
  useEffect(() => {
    if (status !== 'failed') return undefined;
    const left = () =>
      Math.max(0, Math.ceil((limitedUntilRef.current - Date.now()) / 1000));
    setSecondsLeft(left());
    if (left() === 0) return undefined;
    const interval = setInterval(() => {
      const seconds = left();
      setSecondsLeft(seconds);
      if (seconds === 0) clearInterval(interval);
    }, 250);
    return () => clearInterval(interval);
  }, [status]);

  const shuffle = () => {
    if (status === 'loading') return;
    if (Date.now() < limitedUntilRef.current && cycleKept()) return;
    fetchSet();
  };

  const tiles = shown === null ? [] : (sets[shown] ?? []);

  return (
    <div>
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-sm font-medium text-gray-700">
            Pick your security avatar
            {required && <span className="text-red-500"> *</span>}
          </p>
          <p className="mt-0.5 text-xs text-gray-500">
            You&apos;ll see it when you pay on your phone, so you know it&apos;s
            really AyeFace.
          </p>
        </div>
        <button
          type="button"
          onClick={shuffle}
          disabled={status !== 'ready'}
          className="flex shrink-0 items-center gap-1 rounded-md px-2 py-1 text-xs font-semibold text-gray-600 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <Shuffle className="h-3.5 w-3.5" aria-hidden="true" />
          Shuffle
        </button>
      </div>
      {status === 'failed' ? (
        <div className="mt-3 flex aspect-[3/1] flex-col items-center justify-center gap-2 rounded-xl border border-gray-200 bg-gray-50 p-4 text-center">
          <p className="text-sm text-gray-600">
            We couldn&apos;t load the avatars.
          </p>
          <button
            type="button"
            onClick={fetchSet}
            disabled={secondsLeft > 0}
            className="rounded-md px-3 py-1.5 text-sm font-semibold text-[#5CB891] hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {secondsLeft > 0 ? `Try again in ${secondsLeft}s` : 'Try again'}
          </button>
        </div>
      ) : status === 'loading' ? (
        <div
          className="mt-3 grid grid-cols-3 gap-2"
          aria-busy="true"
          aria-label="Loading avatars"
          data-testid="avatar-skeleton"
        >
          {Array.from({ length: TILE_COUNT }, (_, i) => (
            <div
              key={i}
              className="aspect-square animate-pulse rounded-xl border border-gray-200 bg-gray-100"
            />
          ))}
        </div>
      ) : (
        <div className="mt-3 grid grid-cols-3 gap-2">
          {tiles.map((candidate, i) => {
            const selected = candidate.token === value;
            return (
              <button
                key={candidate.token}
                type="button"
                aria-label={`Avatar ${i + 1}`}
                aria-pressed={selected}
                onClick={() => onChange(candidate.token)}
                className={`relative aspect-square overflow-hidden rounded-xl border bg-white p-1 transition-shadow ${
                  selected
                    ? 'border-[#5CB891] ring-2 ring-[#5CB891]'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <img
                  src={candidate.image}
                  alt=""
                  role="img"
                  className="h-full w-full rounded-lg object-cover"
                />
                {selected && (
                  <span className="absolute right-1.5 top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-[#5CB891] text-white">
                    <Check className="h-3 w-3" aria-hidden="true" />
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
